import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CopyLinkButton from '../../components/common/CopyLinkButton';
import DonationMethodForm from './DonationMethodForm';
import donationMethodsService from '../../api/services/donationMethods.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { donationMethodOf, donationMethodLabel } from '../../utils/constants';
import { donationPageLinkUrl } from '../../utils/portalLinks';
import { IconEdit, IconTrash } from '../../components/icons';
import { PATHS } from '../../routes/navConfig';

const PAGE_SIZE = 50;

/* The ways parents can send money to the class fund — its own module, reached
   from the Links hub the same way Sign-Ups is. One master "Donate to <class>"
   link (the public page listing every method) sits on top once there is
   something to list; each saved method is a tile below it. Arriving with
   state.openForm (the hub's "Add Donation Link" tile) opens the form. */
export default function DonationLinksPage() {
  const ctx = useContextStore(selectCurrentContext);
  const location = useLocation();
  const navigate = useNavigate();

  const networkId = ctx?.schoolId ? ctx.id : null;
  const pageUrl = donationPageLinkUrl(networkId);

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [formOpen, setFormOpen] = useState(() => !!location.state?.openForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!networkId) {
      setMethods([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    donationMethodsService
      .list(networkId, { page: 1, limit: PAGE_SIZE })
      .then((r) => { if (!cancelled) setMethods(r.methods); })
      .catch((err) => {
        if (!cancelled) {
          setMethods([]);
          showToast(err.message);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [networkId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const hasMethods = methods.length > 0;

  /* Donation links live under Links — leaving this module lands back there. */
  const goLinks = () => navigate(PATHS.LINKS);

  const openAdd = () => {
    if (!networkId) {
      showToast('Select a class in the header first.');
      return;
    }
    setEditing(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  /* POST for a new method; PUT with only what changed for an edit — the API
     rejects a patch that carries nothing but the id. Changing the method also
     resends the handle so the server re-reads what kind of handle it is. */
  const save = async (fields) => {
    setSaving(true);
    try {
      if (editing) {
        const patch = { id: editing.id };
        if (fields.method !== editing.method) {
          patch.method = fields.method;
          patch.handle = fields.handle;
        } else if (fields.handle !== editing.handle) {
          patch.handle = fields.handle;
        }
        if (fields.recipientName !== editing.recipientName) patch.recipientName = fields.recipientName;
        if (fields.last4 !== editing.last4) patch.last4 = fields.last4;
        const explicitType = donationMethodOf(fields.method).handleType;
        if (explicitType && patch.handle !== undefined) patch.handleType = explicitType;
        if (Object.keys(patch).length === 1) {
          closeForm();
          return;
        }
        await donationMethodsService.update(patch);
        showToast('Method updated ✓');
      } else {
        const explicitType = donationMethodOf(fields.method).handleType;
        await donationMethodsService.create({
          networkId,
          ...fields,
          ...(explicitType ? { handleType: explicitType } : {}),
        });
        showToast('Method added ✓');
      }
      closeForm();
      refresh();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* No delete endpoint — deactivating takes it off the public page and out of
     the listing, which is what Remove means here. */
  const confirmRemove = async () => {
    const d = deleteTarget;
    if (!d) return;
    setDeleting(true);
    try {
      await donationMethodsService.deactivate(d.id);
      setDeleteTarget(null);
      showToast('Method removed ✓');
      refresh();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* "Send to parents" hands off to Compose Email with the master link in the
     draft; `include` must be an object even when empty. */
  const sendToParents = () => {
    navigate(PATHS.EMAIL, {
      state: {
        linkDraft: {
          subject: `How to give to our class fund — ${ctx.name}`,
          body:
            `Hi everyone!\n\nIf you'd like to chip in to our class fund, this link lists every way to give: ${pageUrl}` +
            `\n\nContributions are completely optional — thank you!`,
          include: {},
        },
      },
    });
  };

  return (
    <div className="sec on">
      <SectionHead
        title="Donation Links"
        sub="Give parents a way to give without volunteering time."
        actions={<button className="btn btn-ghost" onClick={goLinks}>← Back to Links</button>}
      />

      <Card>
        {loading && (
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
            <Spinner variant="green" /> Loading donation methods…
          </div>
        )}

        {!loading && !hasMethods && (
          <div className="hint" style={{ marginBottom: 12 }}>
            No donation methods yet — add Zelle, Venmo, or Apple Pay below so parents have a way to give without
            volunteering time. Opening the master link shows a parent every method you've added.
          </div>
        )}

        {hasMethods && (
          <div className="signup-card">
            <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>💳</div>
            <div className="info">
              <div className="t">Donate to {ctx.name}</div>
              <div className="meta">{pageUrl || 'Select a class in the header to get its link'}</div>
            </div>
            <div className="actions">
              <button
                className="btn btn-ghost btn-sm"
                disabled={!pageUrl}
                title="Open the page parents will see"
                onClick={() => window.open(pageUrl, '_blank', 'noopener')}
              >
                Preview
              </button>
              <CopyLinkButton url={pageUrl} />
              <button className="btn btn-primary btn-sm" disabled={!pageUrl} onClick={sendToParents}>
                Send to parents
              </button>
            </div>
          </div>
        )}

        <DonationMethodForm open={formOpen} editing={editing} saving={saving} onClose={closeForm} onSave={save} />

        {hasMethods && (
          <div className="pay-methods">
            {methods.map((d) => {
              const m = donationMethodOf(d.method);
              return (
                <div className="pay-method" key={d.id}>
                  <div className="tools">
                    <div
                      className="pay-edit"
                      title="Edit this method"
                      onClick={() => {
                        setEditing(d);
                        setFormOpen(true);
                      }}
                    >
                      <IconEdit />
                    </div>
                    <div className="ev-trash" title="Remove this method" onClick={() => setDeleteTarget(d)}>
                      <IconTrash />
                    </div>
                  </div>
                  <img src={m.icon} alt="" />
                  <div className="t">{donationMethodLabel(d)}</div>
                  <div className="meta">{[d.recipientName, d.handle].filter(Boolean).join(' · ')}</div>
                  {d.method === 'venmo' && d.last4 && <div className="meta">Phone ends in {d.last4}</div>}
                  <div className="meta">{m.tileSub}</div>
                </div>
              );
            })}
          </div>
        )}

        <button className="btn btn-ghost" onClick={openAdd}>
          + Add Method
        </button>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove Method"
        confirmLabel="Remove"
        busy={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmRemove}
      >
        {deleteTarget && (
          <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
            Parents will no longer see {donationMethodLabel(deleteTarget)} as a way to give. You can add it back any
            time.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
